import { useCallback, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../../../firebase';
import useAgentChatStore from '../../../stores/agentChatStore';
import useDraftBatchStore from '@/stores/draftBatchStore';
import { isSupportedImageFile, normalizeImageFile, resizeImage } from '../../../services/photoStorageService';
import { useSchool } from '../../../contexts/SchoolContext';

// Build the Cloud Function URL from env
const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const REGION = 'africa-south1';
const AGENT_CHAT_URL = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/agentChat`;

/**
 * Hook for sending messages to the Claude agent and parsing SSE responses.
 * Zero external deps — raw fetch + ReadableStream.
 */
export function useAgentChat() {
  const {
    isOpen,
    isStreaming,
    messages,
    error,
    toggle,
    open,
    close,
    setStreaming,
    setError,
    addUserMessage,
    addAssistantMessage,
    appendToAssistant,
    addToolCall,
    addToolResult,
    setResolvedStudents,
    getApiMessages,
    clearMessages,
  } = useAgentChatStore();

  const { schoolId } = useSchool();
  const abortRef = useRef(null);
  const draftBatchIdRef = useRef(null);
  const addDraftBatch = useDraftBatchStore((s) => s.addDraftBatch);

  /**
   * Upload pending files to Firebase Storage and return attachment metadata.
   * Images are resized to 1024px max before upload.
   */
  const uploadFiles = useCallback(
    async (files) => {
      if (!files?.length) return [];

      const attachments = [];
      for (const file of files) {
        const isImage = isSupportedImageFile(file);

        // Resize images to 1024px max
        let uploadData = file;
        let contentType = file.type;
        let uploadFile = file;
        if (isImage) {
          try {
            uploadFile = await normalizeImageFile(file);
            const resized = await resizeImage(uploadFile, 1024);
            uploadData = resized;
            contentType = 'image/jpeg';
          } catch {
            // Fall back to original file
          }
        }

        const timestamp = Date.now();
        const safeName = (uploadFile.name || file.name).replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `schools/${schoolId}/agent-chat/${timestamp}_${safeName}`;
        const storageRef = ref(storage, storagePath);

        const snapshot = await uploadBytes(storageRef, uploadData, {
          contentType: contentType || 'application/octet-stream',
        });
        const url = await getDownloadURL(snapshot.ref);

        attachments.push({
          url,
          name: uploadFile.name || file.name,
          mimeType: contentType || uploadFile.type || file.type || 'application/octet-stream',
        });
      }
      return attachments;
    },
    [schoolId]
  );

  const sendMessage = useCallback(
    async (userText, pendingFiles = []) => {
      if ((!userText.trim() && !pendingFiles.length) || isStreaming) return;

      setStreaming(true);
      setError(null);

      try {
        // Upload files first
        const attachments = await uploadFiles(pendingFiles);

        // Add user message to store (with attachments if any)
        addUserMessage(userText.trim(), attachments.length ? attachments : null);

        // Get conversation history for API (includes the new message)
        const apiMessages = [...getApiMessages()];

        // Start assistant message
        addAssistantMessage();

        // Get Firebase auth token
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Not authenticated');
        }
        const token = await currentUser.getIdToken();

        // Create abort controller
        const controller = new AbortController();
        abortRef.current = controller;

        // Generate a draftBatchId for this assistant turn
        const batchId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        draftBatchIdRef.current = batchId;

        // Read agent chat provider config from localStorage
        const provider = localStorage.getItem('swan_agent_provider') || 'gemini';
        const apiKey = localStorage.getItem('swan_agent_api_key') || '';
        const model = localStorage.getItem('swan_agent_model') || '';

        const response = await fetch(AGENT_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: apiMessages,
            provider,
            ...(apiKey && { apiKey }),
            ...(model && { model }),
            draftBatchId: batchId,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `HTTP ${response.status}`);
        }

        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          let eventType = null;
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const dataStr = line.slice(6);
              try {
                const data = JSON.parse(dataStr);
                handleSSEEvent(eventType, data);
              } catch {
                // Skip malformed JSON
              }
              eventType = null;
            }
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled — not an error
          return;
        }
        console.error('[useAgentChat] Error:', err);
        setError(err.message);
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [isStreaming, addUserMessage, addAssistantMessage, appendToAssistant, setStreaming, setError, getApiMessages, uploadFiles]
  );

  const handleSSEEvent = useCallback(
    (eventType, data) => {
      switch (eventType) {
        case 'text_delta':
          if (data.text) {
            appendToAssistant(data.text);
          }
          break;
        case 'tool_start':
          if (data.toolName) {
            addToolCall(data.toolName, data.input);
          }
          break;
        case 'tool_result':
          if (data.toolName && data.result) {
            addToolResult(data.toolName, data.result);

            // Track draft items in the batch store
            if (data.result._draft && data.result._draftBatchId) {
              addDraftBatch(data.result._draftBatchId, [{
                _collection: data.result._collection,
                _docId: data.result._docId,
                toolName: data.toolName,
                summary: data.result.summary || null,
              }]);

              // Store the batchId on the current assistant message for DraftReviewPanel
              useAgentChatStore.getState().setDraftBatchId(data.result._draftBatchId);
            }
          }
          break;
        case 'resolved_students':
          if (data.students?.length) {
            setResolvedStudents(data.students);
          }
          break;
        case 'error':
          setError(data.message || 'Unknown error');
          break;
        case 'done':
          // Stream complete
          break;
        default:
          break;
      }
    },
    [appendToAssistant, addToolCall, addToolResult, setResolvedStudents, setError, addDraftBatch]
  );

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      setStreaming(false);
    }
  }, [setStreaming]);

  return {
    isOpen,
    isStreaming,
    messages,
    error,
    toggle,
    open,
    close,
    sendMessage,
    cancel,
    clearMessages,
  };
}
